import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabase';
import {
  bodyByteLength,
  PERSONAL_TODOS_BODY_HARD_CAP_BYTES,
  PERSONAL_TODOS_DEFAULT_STATE,
  PERSONAL_TODOS_RECENT_DONE_WINDOW_MS,
  matchesRowFilters,
  parsePersonalTodosSearchParams,
  sortPersonalTodos,
  type PersonalTodoPriority,
  type PersonalTodoRow,
  type PersonalTodosListState
} from '../models/personalTodos';

/**
 * ViewModel for `/personal-todos`.
 *
 * Owns PostgREST loads, client-side filter/sort derivation, and mutation
 * actions. Exposes `window.__penrosePersonalTodos` for Playwright.
 */

export type PersonalTodosLoadStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'unauth'
  | 'error';

export interface PersonalTodosVMState {
  openTodos: ReadonlyArray<PersonalTodoRow>;
  recentlyDone: ReadonlyArray<PersonalTodoRow>;
  archived: ReadonlyArray<PersonalTodoRow>;
  loadStatus: PersonalTodosLoadStatus;
  error: string | null;
}

export interface PersonalTodosVMDerived {
  listState: PersonalTodosListState;
  includeArchived: boolean;
  sortLocked: boolean;
  isEmpty: boolean;
  isFilteredEmpty: boolean;
  hasAnyTodos: boolean;
  filteredOpen: ReadonlyArray<PersonalTodoRow>;
  filteredDone: ReadonlyArray<PersonalTodoRow>;
  filteredArchived: ReadonlyArray<PersonalTodoRow>;
  showOpenSection: boolean;
  showDoneSection: boolean;
  doneSectionLabel: string;
}

export interface PersonalTodosVMActions {
  reload: () => void;
  quickAdd: (title: string) => Promise<void>;
  toggleDone: (id: string) => Promise<void>;
  setTitle: (id: string, title: string) => Promise<void>;
  setBody: (id: string, body: string) => Promise<void>;
  saveContent: (
    id: string,
    content: { title: string; body: string }
  ) => Promise<boolean>;
  setPriority: (id: string, prio: PersonalTodoPriority) => Promise<void>;
  setDueDate: (id: string, date: string | null) => Promise<void>;
  archive: (id: string) => Promise<void>;
  restore: (id: string) => Promise<void>;
  moveUp: (id: string) => Promise<void>;
  moveDown: (id: string) => Promise<void>;
}

export interface UsePersonalTodosVMInput {
  viewerId: string | null;
  searchParams: URLSearchParams;
}

export interface UsePersonalTodosVM {
  state: PersonalTodosVMState;
  derived: PersonalTodosVMDerived;
  actions: PersonalTodosVMActions;
}

function sortOpen(rows: ReadonlyArray<PersonalTodoRow>): ReadonlyArray<PersonalTodoRow> {
  return sortPersonalTodos(rows, 'manual', 'open');
}

function sortRecentlyDone(
  rows: ReadonlyArray<PersonalTodoRow>
): ReadonlyArray<PersonalTodoRow> {
  return sortPersonalTodos(rows, 'manual', 'done');
}

function sortArchived(
  rows: ReadonlyArray<PersonalTodoRow>
): ReadonlyArray<PersonalTodoRow> {
  return sortPersonalTodos(rows, 'manual', 'archived');
}

function deriveFilteredSections(
  openTodos: ReadonlyArray<PersonalTodoRow>,
  recentlyDone: ReadonlyArray<PersonalTodoRow>,
  archived: ReadonlyArray<PersonalTodoRow>,
  listState: PersonalTodosListState
): Pick<
  PersonalTodosVMDerived,
  | 'filteredOpen'
  | 'filteredDone'
  | 'filteredArchived'
  | 'showOpenSection'
  | 'showDoneSection'
  | 'doneSectionLabel'
  | 'isFilteredEmpty'
  | 'hasAnyTodos'
> {
  const { status, sort } = listState;

  let open: PersonalTodoRow[] = openTodos
    .filter((r) => matchesRowFilters(r, listState))
    .slice();
  let done: PersonalTodoRow[] = recentlyDone
    .filter((r) => matchesRowFilters(r, listState))
    .slice();
  let arch: PersonalTodoRow[] = archived
    .filter((r) => matchesRowFilters(r, listState))
    .slice();

  open = [...sortPersonalTodos(open, sort, 'open')];
  done = [...sortPersonalTodos(done, sort, 'done')];
  arch = [...sortPersonalTodos(arch, sort, 'archived')];

  const showOpenSection = status === 'open' || status === 'all';
  const showDoneSection =
    status === 'done' ||
    status === 'all' ||
    (status === 'open' && done.length > 0);

  const doneSectionLabel =
    status === 'open' ? 'Recently done (last 7 days)' : 'Done';

  const hasAnyTodos =
    openTodos.length > 0 || recentlyDone.length > 0 || archived.length > 0;

  const anyVisible =
    (showOpenSection && open.length > 0) ||
    (showDoneSection && done.length > 0) ||
    (listState.includeArchived && arch.length > 0);

  const isFilteredEmpty = hasAnyTodos && !anyVisible;

  return {
    filteredOpen: open,
    filteredDone: done,
    filteredArchived: arch,
    showOpenSection,
    showDoneSection,
    doneSectionLabel,
    isFilteredEmpty,
    hasAnyTodos
  };
}

export function usePersonalTodosVM(
  input: UsePersonalTodosVMInput
): UsePersonalTodosVM {
  const { viewerId, searchParams } = input;

  const listState = useMemo<PersonalTodosListState>(() => {
    return parsePersonalTodosSearchParams(searchParams);
  }, [searchParams]);

  const [openTodos, setOpenTodos] = useState<ReadonlyArray<PersonalTodoRow>>(
    []
  );
  const [recentlyDone, setRecentlyDone] = useState<
    ReadonlyArray<PersonalTodoRow>
  >([]);
  const [archived, setArchived] = useState<ReadonlyArray<PersonalTodoRow>>([]);
  const [loadStatus, setLoadStatus] = useState<PersonalTodosLoadStatus>(
    viewerId ? 'loading' : 'idle'
  );
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  const mountedRef = useRef(true);
  const activeEpochRef = useRef(0);
  const archivedEpochRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!viewerId) {
      setLoadStatus('unauth');
      setOpenTodos([]);
      setRecentlyDone([]);
      return;
    }
    const token = ++activeEpochRef.current;
    setLoadStatus('loading');
    setError(null);
    void (async () => {
      const { data, error: fetchErr } = await supabase
        .from('personal_todos')
        .select('*')
        .eq('user_id', viewerId)
        .is('archived_at', null)
        .order('position', { ascending: true })
        .order('created_at', { ascending: false });

      if (!mountedRef.current || token !== activeEpochRef.current) return;
      if (fetchErr) {
        setLoadStatus('error');
        setError("Couldn't load your todos. Please try again.");
        return;
      }

      const rows = (data ?? []) as ReadonlyArray<PersonalTodoRow>;
      const open: PersonalTodoRow[] = [];
      const recent: PersonalTodoRow[] = [];
      const cutoffMs = Date.now() - PERSONAL_TODOS_RECENT_DONE_WINDOW_MS;
      for (const r of rows) {
        if (r.status === 'open') {
          open.push(r);
          continue;
        }
        if (r.status === 'done') {
          const doneMs = r.done_at ? Date.parse(r.done_at) : NaN;
          if (Number.isFinite(doneMs) && doneMs >= cutoffMs) {
            recent.push(r);
          }
        }
      }

      setOpenTodos(sortOpen(open));
      setRecentlyDone(sortRecentlyDone(recent));
      setLoadStatus('ready');
    })();
  }, [viewerId, reloadTick]);

  useEffect(() => {
    if (!viewerId) {
      setArchived([]);
      return;
    }
    if (!listState.includeArchived) {
      setArchived([]);
      return;
    }
    const token = ++archivedEpochRef.current;
    void (async () => {
      const { data, error: fetchErr } = await supabase
        .from('personal_todos')
        .select('*')
        .eq('user_id', viewerId)
        .eq('status', 'archived')
        .order('archived_at', { ascending: false });

      if (!mountedRef.current || token !== archivedEpochRef.current) return;
      if (fetchErr) {
        setError("Couldn't load your archived todos. Please try again.");
        return;
      }

      const rows = (data ?? []) as ReadonlyArray<PersonalTodoRow>;
      setArchived(sortArchived(rows));
    })();
  }, [viewerId, listState.includeArchived, reloadTick]);

  const reload = useCallback<PersonalTodosVMActions['reload']>(() => {
    setReloadTick((n) => n + 1);
  }, []);

  const openRef = useRef(openTodos);
  openRef.current = openTodos;
  const recentRef = useRef(recentlyDone);
  recentRef.current = recentlyDone;
  const archivedRef = useRef(archived);
  archivedRef.current = archived;
  const listStateRef = useRef(listState);
  listStateRef.current = listState;

  const setMutationError = useCallback((msg: string) => {
    setError(msg);
  }, []);

  const quickAdd = useCallback<PersonalTodosVMActions['quickAdd']>(
    async (title) => {
      if (!viewerId) return;
      const trimmed = title.trim();
      if (trimmed.length === 0) return;
      const positions = openRef.current.map((r) => r.position);
      const nextPosition =
        positions.length === 0 ? 0 : Math.min(...positions) - 1;
      const tempId = `pt-temp-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const nowIso = new Date().toISOString();
      const optimistic: PersonalTodoRow = {
        id: tempId,
        user_id: viewerId,
        title: trimmed,
        body: '',
        priority: 'medium',
        status: 'open',
        due_date: null,
        done_at: null,
        archived_at: null,
        position: nextPosition,
        created_at: nowIso,
        updated_at: nowIso
      };
      const prevOpen = openRef.current;
      setOpenTodos(sortOpen([optimistic, ...prevOpen]));

      const { data, error: insErr } = await supabase
        .from('personal_todos')
        .insert({
          user_id: viewerId,
          title: trimmed,
          status: 'open',
          priority: 'medium',
          position: nextPosition
        })
        .select('*')
        .single();

      if (!mountedRef.current) return;
      if (insErr || !data) {
        setOpenTodos(prevOpen);
        setMutationError("Couldn't add the todo. Please try again.");
        return;
      }
      setOpenTodos((rows) =>
        sortOpen(
          rows.map((r) => (r.id === tempId ? (data as PersonalTodoRow) : r))
        )
      );
    },
    [viewerId, setMutationError]
  );

  const toggleDone = useCallback<PersonalTodosVMActions['toggleDone']>(
    async (id) => {
      if (!viewerId) return;
      const openRow = openRef.current.find((r) => r.id === id) ?? null;
      const doneRow = recentRef.current.find((r) => r.id === id) ?? null;
      if (!openRow && !doneRow) return;

      const prevOpen = openRef.current;
      const prevRecent = recentRef.current;

      if (openRow) {
        const nowIso = new Date().toISOString();
        const movedRow: PersonalTodoRow = {
          ...openRow,
          status: 'done',
          done_at: nowIso
        };
        setOpenTodos(prevOpen.filter((r) => r.id !== id));
        setRecentlyDone(sortRecentlyDone([movedRow, ...prevRecent]));

        const { error: updErr } = await supabase
          .from('personal_todos')
          .update({ status: 'done', done_at: nowIso })
          .eq('id', id);
        if (!mountedRef.current) return;
        if (updErr) {
          setOpenTodos(prevOpen);
          setRecentlyDone(prevRecent);
          setMutationError("Couldn't mark the todo done. Please try again.");
        }
        return;
      }

      if (doneRow) {
        const reopened: PersonalTodoRow = {
          ...doneRow,
          status: 'open',
          done_at: null
        };
        setRecentlyDone(prevRecent.filter((r) => r.id !== id));
        setOpenTodos(sortOpen([reopened, ...prevOpen]));

        const { error: updErr } = await supabase
          .from('personal_todos')
          .update({ status: 'open', done_at: null })
          .eq('id', id);
        if (!mountedRef.current) return;
        if (updErr) {
          setOpenTodos(prevOpen);
          setRecentlyDone(prevRecent);
          setMutationError("Couldn't reopen the todo. Please try again.");
        }
      }
    },
    [viewerId, setMutationError]
  );

  const setTitle = useCallback<PersonalTodosVMActions['setTitle']>(
    async (id, title) => {
      if (!viewerId) return;
      const trimmed = title.trim() || 'Untitled todo';
      const prevOpen = openRef.current;
      const prevRecent = recentRef.current;
      const inOpen = prevOpen.find((r) => r.id === id);
      const inRecent = prevRecent.find((r) => r.id === id);
      if (!inOpen && !inRecent) return;

      if (inOpen) {
        setOpenTodos(
          prevOpen.map((r) => (r.id === id ? { ...r, title: trimmed } : r))
        );
      } else if (inRecent) {
        setRecentlyDone(
          prevRecent.map((r) => (r.id === id ? { ...r, title: trimmed } : r))
        );
      }

      const { error: updErr } = await supabase
        .from('personal_todos')
        .update({ title: trimmed })
        .eq('id', id);
      if (!mountedRef.current) return;
      if (updErr) {
        setOpenTodos(prevOpen);
        setRecentlyDone(prevRecent);
        setMutationError("Couldn't update the title. Please try again.");
      }
    },
    [viewerId, setMutationError]
  );

  const setBody = useCallback<PersonalTodosVMActions['setBody']>(
    async (id, body) => {
      if (!viewerId) return;
      const prevOpen = openRef.current;
      const prevRecent = recentRef.current;
      const inOpen = prevOpen.find((r) => r.id === id);
      const inRecent = prevRecent.find((r) => r.id === id);
      if (!inOpen && !inRecent) return;

      if (inOpen) {
        setOpenTodos(
          prevOpen.map((r) => (r.id === id ? { ...r, body } : r))
        );
      } else if (inRecent) {
        setRecentlyDone(
          prevRecent.map((r) => (r.id === id ? { ...r, body } : r))
        );
      }

      const { error: updErr } = await supabase
        .from('personal_todos')
        .update({ body })
        .eq('id', id);
      if (!mountedRef.current) return;
      if (updErr) {
        setOpenTodos(prevOpen);
        setRecentlyDone(prevRecent);
        setMutationError("Couldn't save the notes. Please try again.");
      }
    },
    [viewerId, setMutationError]
  );

  const saveContent = useCallback<PersonalTodosVMActions['saveContent']>(
    async (id, content) => {
      if (!viewerId) return false;
      const trimmed = content.title.trim() || 'Untitled todo';
      if (bodyByteLength(content.body) >= PERSONAL_TODOS_BODY_HARD_CAP_BYTES) {
        return false;
      }

      const prevOpen = openRef.current;
      const prevRecent = recentRef.current;
      const inOpen = prevOpen.find((r) => r.id === id);
      const inRecent = prevRecent.find((r) => r.id === id);
      if (!inOpen && !inRecent) return false;

      if (inOpen) {
        setOpenTodos(
          prevOpen.map((r) =>
            r.id === id ? { ...r, title: trimmed, body: content.body } : r
          )
        );
      } else if (inRecent) {
        setRecentlyDone(
          prevRecent.map((r) =>
            r.id === id ? { ...r, title: trimmed, body: content.body } : r
          )
        );
      }

      const { error: updErr } = await supabase
        .from('personal_todos')
        .update({ title: trimmed, body: content.body })
        .eq('id', id);
      if (!mountedRef.current) return false;
      if (updErr) {
        setOpenTodos(prevOpen);
        setRecentlyDone(prevRecent);
        setMutationError("Couldn't save the todo. Please try again.");
        return false;
      }
      return true;
    },
    [viewerId, setMutationError]
  );

  const setPriority = useCallback<PersonalTodosVMActions['setPriority']>(
    async (id, prio) => {
      if (!viewerId) return;
      const prevOpen = openRef.current;
      const target = prevOpen.find((r) => r.id === id);
      if (!target) return;
      setOpenTodos(
        sortOpen(
          prevOpen.map((r) => (r.id === id ? { ...r, priority: prio } : r))
        )
      );
      const { error: updErr } = await supabase
        .from('personal_todos')
        .update({ priority: prio })
        .eq('id', id);
      if (!mountedRef.current) return;
      if (updErr) {
        setOpenTodos(prevOpen);
        setMutationError("Couldn't update priority. Please try again.");
      }
    },
    [viewerId, setMutationError]
  );

  const setDueDate = useCallback<PersonalTodosVMActions['setDueDate']>(
    async (id, date) => {
      if (!viewerId) return;
      const prevOpen = openRef.current;
      const target = prevOpen.find((r) => r.id === id);
      if (!target) return;
      setOpenTodos(
        prevOpen.map((r) => (r.id === id ? { ...r, due_date: date } : r))
      );
      const { error: updErr } = await supabase
        .from('personal_todos')
        .update({ due_date: date })
        .eq('id', id);
      if (!mountedRef.current) return;
      if (updErr) {
        setOpenTodos(prevOpen);
        setMutationError("Couldn't update the due date. Please try again.");
      }
    },
    [viewerId, setMutationError]
  );

  const archive = useCallback<PersonalTodosVMActions['archive']>(
    async (id) => {
      if (!viewerId) return;
      const prevOpen = openRef.current;
      const prevRecent = recentRef.current;
      const prevArchived = archivedRef.current;
      const fromOpen = prevOpen.find((r) => r.id === id) ?? null;
      const fromRecent = prevRecent.find((r) => r.id === id) ?? null;
      const target = fromOpen ?? fromRecent;
      if (!target) return;
      const nowIso = new Date().toISOString();
      const archivedRow: PersonalTodoRow = {
        ...target,
        status: 'archived',
        archived_at: nowIso
      };
      if (fromOpen) setOpenTodos(prevOpen.filter((r) => r.id !== id));
      if (fromRecent) setRecentlyDone(prevRecent.filter((r) => r.id !== id));
      setArchived(sortArchived([archivedRow, ...prevArchived]));

      const { error: updErr } = await supabase
        .from('personal_todos')
        .update({ status: 'archived', archived_at: nowIso })
        .eq('id', id);
      if (!mountedRef.current) return;
      if (updErr) {
        setOpenTodos(prevOpen);
        setRecentlyDone(prevRecent);
        setArchived(prevArchived);
        setMutationError("Couldn't archive the todo. Please try again.");
      }
    },
    [viewerId, setMutationError]
  );

  const restore = useCallback<PersonalTodosVMActions['restore']>(
    async (id) => {
      if (!viewerId) return;
      const prevOpen = openRef.current;
      const prevArchived = archivedRef.current;
      const target = prevArchived.find((r) => r.id === id);
      if (!target) return;

      const restored: PersonalTodoRow = {
        ...target,
        status: 'open',
        archived_at: null,
        done_at: null
      };
      setArchived(prevArchived.filter((r) => r.id !== id));
      setOpenTodos(sortOpen([restored, ...prevOpen]));

      const { error: updErr } = await supabase
        .from('personal_todos')
        .update({
          status: 'open',
          archived_at: null,
          done_at: null
        })
        .eq('id', id);
      if (!mountedRef.current) return;
      if (updErr) {
        setOpenTodos(prevOpen);
        setArchived(prevArchived);
        setMutationError("Couldn't restore the todo. Please try again.");
      }
    },
    [viewerId, setMutationError]
  );

  const swapPositions = useCallback(
    async (id: string, direction: 'up' | 'down') => {
      if (!viewerId) return;
      if (listStateRef.current.sort !== 'manual') return;
      const prevOpen = openRef.current;
      const idx = prevOpen.findIndex((r) => r.id === id);
      if (idx === -1) return;
      const neighborIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (neighborIdx < 0 || neighborIdx >= prevOpen.length) return;
      const row = prevOpen[idx];
      const neighbor = prevOpen[neighborIdx];

      const rowSwapped: PersonalTodoRow = { ...row, position: neighbor.position };
      const neighborSwapped: PersonalTodoRow = {
        ...neighbor,
        position: row.position
      };
      const nextRows = prevOpen.map((r) => {
        if (r.id === row.id) return rowSwapped;
        if (r.id === neighbor.id) return neighborSwapped;
        return r;
      });
      setOpenTodos(sortOpen(nextRows));

      const first = await supabase
        .from('personal_todos')
        .update({ position: neighbor.position })
        .eq('id', row.id);
      const second = await supabase
        .from('personal_todos')
        .update({ position: row.position })
        .eq('id', neighbor.id);
      if (!mountedRef.current) return;
      if (first.error || second.error) {
        setOpenTodos(prevOpen);
        setMutationError("Couldn't reorder the todos. Please try again.");
      }
    },
    [viewerId, setMutationError]
  );

  const moveUp = useCallback<PersonalTodosVMActions['moveUp']>(
    (id) => swapPositions(id, 'up'),
    [swapPositions]
  );
  const moveDown = useCallback<PersonalTodosVMActions['moveDown']>(
    (id) => swapPositions(id, 'down'),
    [swapPositions]
  );

  const actions = useMemo<PersonalTodosVMActions>(
    () => ({
      reload,
      quickAdd,
      toggleDone,
      setTitle,
      setBody,
      saveContent,
      setPriority,
      setDueDate,
      archive,
      restore,
      moveUp,
      moveDown
    }),
    [
      reload,
      quickAdd,
      toggleDone,
      setTitle,
      setBody,
      saveContent,
      setPriority,
      setDueDate,
      archive,
      restore,
      moveUp,
      moveDown
    ]
  );

  const state = useMemo<PersonalTodosVMState>(
    () => ({ openTodos, recentlyDone, archived, loadStatus, error }),
    [openTodos, recentlyDone, archived, loadStatus, error]
  );

  const derived = useMemo<PersonalTodosVMDerived>(() => {
    const filtered = deriveFilteredSections(
      openTodos,
      recentlyDone,
      archived,
      listState
    );
    return {
      listState,
      includeArchived: listState.includeArchived,
      sortLocked: listState.sort !== 'manual',
      isEmpty:
        loadStatus === 'ready' &&
        openTodos.length === 0 &&
        recentlyDone.length === 0 &&
        archived.length === 0,
      ...filtered
    };
  }, [listState, loadStatus, openTodos, recentlyDone, archived]);

  const stateRef = useRef(state);
  stateRef.current = state;
  const derivedRef = useRef(derived);
  derivedRef.current = derived;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handle = {
      getState: () => ({
        ...stateRef.current,
        ...derivedRef.current
      }),
      reload,
      quickAdd,
      toggleDone,
      setTitle,
      setBody,
      saveContent,
      setPriority,
      setDueDate,
      archive,
      restore,
      moveUp,
      moveDown
    };
    (window as unknown as {
      __penrosePersonalTodos?: typeof handle;
    }).__penrosePersonalTodos = handle;
    return () => {
      delete (window as unknown as {
        __penrosePersonalTodos?: typeof handle;
      }).__penrosePersonalTodos;
    };
  }, [
    reload,
    quickAdd,
    toggleDone,
    setTitle,
    setBody,
    saveContent,
    setPriority,
    setDueDate,
    archive,
    restore,
    moveUp,
    moveDown
  ]);

  return { state, derived, actions };
}

export { PERSONAL_TODOS_DEFAULT_STATE };
